import type { Locale, TranslationTree } from "@llmgateway/i18n";

export const playgroundMessages = {
	en: {
		common: {
			add: "Add",
			back: "Back",
			cancel: "Cancel",
			clear: "Clear",
			close: "Close",
			continue: "Continue",
			creating: "Creating...",
			default: "Default",
			generate: "Generate",
			generating: "Generating...",
			loading: "Loading...",
			or: "Or",
			processing: "Processing...",
			redirecting: "Redirecting...",
			unexpectedError: "An unexpected error occurred. Please try again.",
			validating: "Validating...",
		},
		language: {
			short: "EN",
		},
		auth: {
			accountCreated: "Account created",
			accountCreatedDescription:
				"Please check your email to verify your account before signing in.",
			alreadyHaveAccount: "Already have an account? Sign in",
			alreadyHaveAccountQuestion: "Already have an account?",
			createAccount: "Create account",
			createAccountLoading: "Creating account...",
			createAccountSubtitle:
				"Start chatting with the world's best models in seconds",
			dontHaveAccount: "Don't have an account? Sign up",
			emailInvalid: "Please enter a valid email address",
			loginBrandLine1: "Welcome back.",
			loginBrandLine2: "Pick up the thread.",
			loginBrandSubtitle:
				"Your chats, studios, and favorite models are right where you left them.",
			loginSuccess: "Login successful",
			nameOptional: "Name (optional)",
			passkeyFailed: "Failed to sign in with passkey",
			passwordMin: "Password must be at least 8 characters",
			passwordRequired: "Password is required",
			signIn: "Sign in",
			signInSubtitle: "Sign in to continue your chats",
			signInWithPasskey: "Sign in with passkey",
			signUpFailed: "Failed to sign up",
			signingIn: "Signing in...",
			startFree: "Start free",
			signupBrandLine1: "Every model.",
			signupBrandLine2: "One chat.",
			signupBrandSubtitle:
				"Chat with GPT, Claude, Gemini, and hundreds more. Compare answers side by side, then create images, video, and audio - all with one account.",
			socialSignInFailed: "Failed to sign in with {provider}",
		},
		billing: {
			addCreditsDescription:
				"Add credits to your organization account. Confirm details on the next step.",
			addNewPaymentMethod: "Add New Payment Method",
			amountSummary: "Amount: ${amount}",
			amountWholeDollar: "Amount must be a whole dollar amount.",
			amountUsd: "Amount (USD)",
			cardDetails: "Card Details",
			chatPlanLeft: "${remaining} of ${limit} left",
			checkoutFailed: "Checkout Failed",
			checkoutFailedDescription: "Failed to create checkout session.",
			confirmPayment: "Confirm Payment",
			couldNotReadCardDetails: "Could not read card details.",
			credits: "Credits",
			creditsAdded: "Your credits have been added to your account.",
			defaultPaymentMethod: "Default",
			calculatingFees: "Calculating fees...",
			cardPaymentsUnavailable:
				"Card payments are unavailable in this deployment.",
			error: "Error",
			errorSavingCard: "Error Saving Card",
			expires: "Expires {month}/{year}",
			feeBreakdown: "Fee Breakdown",
			internationalCardFee: "International card fee (1.5%)",
			internationalCardsFeeNote:
				"International cards are subject to an additional 1.5% processing fee.",
			invoiceSettingsNote:
				"Need company/address details on your invoice? Update billing settings before purchase. We email the invoice automatically after payment.",
			loadingPaymentForm: "Loading payment form...",
			lowCredits: "Low credits remaining",
			maximumTopUp: "Maximum top-up amount is ${amount}.",
			minMaxTopUp: "Minimum ${min}. Maximum ${max}.",
			minimumTopUp: "Minimum top-up amount is ${amount}.",
			noCredits: "No credits remaining",
			payWithCard: "Pay with Card",
			payWithAlipay: "Pay with Alipay",
			payWithStripeCheckout: "Pay with Stripe Checkout",
			paymentDetails: "Payment Details",
			paymentFailed: "Payment Failed",
			paymentFailedDescription:
				"An error occurred while processing your payment.",
			paymentMethodsDescription:
				"Choose a payment method to add ${amount} credits. Confirm details on the next step.",
			paymentSummary: "Payment Summary",
			paymentSuccessful: "Payment Successful",
			payAmount: "Pay ${amount}",
			platformFee: "Platform fee (5%)",
			reviewPaymentDetails: "Review your payment details before confirming.",
			saveCard: "Save this card for future payments",
			selectPaymentMethod: "Select Payment Method",
			stripeCheckoutMethods:
				"Stripe Checkout supports additional payment methods like Google Pay, Apple Pay, and more.",
			thankYouPurchase:
				"Thank you for your purchase. Your credits are now available for use.",
			topUpCredits: "Top Up Credits",
			total: "Total",
			upgradeCredits: "Get up to 3x the credits",
			upgradeMonthly: "Upgrade to a monthly plan · from $9/mo",
			useCardDescription: "Enter your card details to add ${amount} credits.",
		},
		pricing: {
			backToChat: "Back to chat",
			billingCycleNotice:
				"Credits reset every billing cycle and don't roll over. Pay-as-you-go credits stay available - chat plan credits drain first on requests from the chat app.",
			bulletCancelBody:
				"Subscription stays active until the end of the period you already paid for.",
			bulletCancelTitle: "Cancel anytime.",
			bulletExpireBody:
				"Each cycle you get a fresh allowance - unused credits don't roll over.",
			bulletExpireTitle: "Credits expire at month end.",
			bulletFrontierBody:
				"(Opus, GPT-5, Gemini 2.5 Pro, Grok 4). Plus and Pro include everything.",
			bulletFrontierTitle: "Starter excludes frontier models",
			bulletTopUpBody:
				"Top-ups never expire and act as a fallback once monthly credits are spent.",
			bulletTopUpTitle: "Your pay-as-you-go balance stays.",
			cancelPlan: "Cancel plan",
			cancelledSuccess: "Plan cancelled - access continues until period end",
			cancellationFailed: "Cancellation failed",
			changeFailed: "Change failed",
			changeUnavailable: "Plan changes unavailable",
			changeUnavailableHint:
				"This chat plan is billed through Alipay. To switch tiers, cancel it at period end and subscribe to the new tier.",
			confirmCancel:
				"Cancel your chat plan? You'll keep access until the end of the current cycle.",
			currentPeriod: " · {action} {date}",
			currentPlan: "Current plan",
			currentPlanCta: "Current plan",
			currentUsage: "${used} of ${limit} used",
			getPlan: "Get {plan}",
			headline: "Three plans. Frontier models on Plus and Pro.",
			heroPrefix: "Subscribe and get",
			heroStrong: "up to 3x the credits for your dollar",
			heroSuffix:
				". Credits reset each cycle - pay-as-you-go top-ups stay available alongside.",
			howItWorks: "How it works",
			invoiceInfo:
				"Need company/address details on your invoice? Update billing settings before purchase. We email the invoice automatically after payment.",
			metadataDescription:
				"Subscribe to a chat plan and get up to 3x the credits for your dollar. All frontier models in one place, billed monthly.",
			metadataTitle: "Pricing - doteb Chat",
			mostPopular: "Most popular",
			paidShort: "${amount} paid",
			perMonth: "/mo",
			periodEnds: "ends",
			periodRenews: "renews",
			plans: {
				starter: {
					name: "Starter",
					description: "For occasional, light chat use",
					tagline: "Get a feel for every model",
					featureModels:
						"Most chat models - Claude Haiku, Sonnet, GPT-5-mini, Gemini Flash, ...",
					featureChat: "Web-based chat at chat.doteb.com",
					featureUsage: "Real-time usage and per-message cost",
					featureSwitch: "Switch tiers any time - prorated",
				},
				plus: {
					name: "Plus",
					description: "Just above ChatGPT Plus - but every frontier model",
					tagline: "The everyday default",
					featureStarter: "Everything in Starter, plus frontier models",
					featureModels:
						"Claude Opus, GPT-5, Gemini 2.5 Pro, Grok 4 - all included",
					featureHeadroom: "Headroom for long daily sessions",
					featureSupport: "Email support",
				},
				pro: {
					name: "Pro",
					description: "For power users who chat all day",
					tagline: "Heaviest use, best $/usage",
					featurePlus: "Everything in Plus",
					featureAllowance: "The largest monthly credit allowance - 3x value",
					featureSupport: "Priority support",
					featureValue: "Best value at high volume",
				},
			},
			providerRates: "at provider rates",
			redirecting: "Redirecting...",
			resume: "Resume",
			resumeFailed: "Resume failed",
			resumedSuccess: "Plan resumed",
			subscribeFailed: "Subscribe failed",
			switchedTo: "Switched to {plan}",
			switching: "Switching...",
			switchToPlan: "Switch to {plan}",
			usedShort: "${amount} used",
			whatYouGet: "What you actually get",
			multiplierValue: "{multiplier}x value",
			youPay: "You pay",
			youUse: "You use",
			yourPlan: "Your plan",
		},
		aiElements: {
			addPhotosOrFiles: "Add photos or files",
			attachmentAlt: "Attachment",
			branchPage: "{current} of {total}",
			cache: "Cache",
			console: "Console",
			context: "Context",
			copyModelId: "Copy model ID",
			error: "Error",
			free: "FREE",
			generatedImage: "Generated image {index}",
			imageModelsFound: "{count} image models found",
			input: "Input",
			inputShort: "in",
			messagePlaceholder: "What would you like to know?",
			modelContextUsage: "Model context usage",
			modelsFound: "{count} models found",
			next: "Next",
			nextBranch: "Next branch",
			noAcceptedFiles: "No files match the accepted types.",
			noConsoleOutput: "No console output",
			output: "Output",
			outputShort: "out",
			parameters: "Parameters",
			perRequest: "/ request",
			previous: "Previous",
			previousBranch: "Previous branch",
			price: "Price",
			pricing: "Pricing",
			providers: "Providers",
			reasoning: "Reasoning",
			removeAttachment: "Remove attachment",
			result: "Result",
			statusApprovalRequested: "Approval Requested",
			statusApprovalResponded: "Approval Responded",
			statusCompleted: "Completed",
			statusDenied: "Denied",
			statusPending: "Pending",
			statusRunning: "Running",
			submit: "Submit",
			thoughtFewSeconds: "Thought for a few seconds",
			thoughtSeconds: "Thought for {duration} seconds",
			thinking: "Thinking...",
			tooManyFiles: "Too many files. Some were not added.",
			totalCost: "Total cost",
			unknown: "unknown",
			unknownFile: "Unknown file",
			uploadFiles: "Upload files",
			urlPlaceholder: "Enter URL...",
			webPreviewTitle: "Preview",
			filesExceedMaxSize: "All files exceed the maximum size.",
		},
		modelSelector: {
			allModels: "All models",
			autoSelectProvider: "Auto-select provider",
			capabilities: "Capabilities",
			clearAll: "Clear all",
			clearFilters: "Clear filters to see all models",
			filters: "Filters",
			freeModels: "Free models",
			from: "From",
			hideUnstable: "Hide unstable/experimental",
			highCost: "High cost (> $0.00001)",
			lowCost: "Low cost (≤ $0.000001)",
			mediumCost: "Medium cost (≤ $0.00001)",
			modelDetails: "Model details",
			modelsFound: "{count} models found",
			noModelsFound: "No models found.",
			priceRange: "Price Range",
			pricingLimits: "Pricing & Limits",
			providers: "Providers",
			addModel: "Add a model...",
			addToFavorites: "Add to favorites",
			cachedInput: "Cached Input",
			capabilityImageGeneration: "Image Generation",
			capabilityJsonOutput: "JSON Output",
			capabilityReasoning: "Reasoning",
			capabilityStreaming: "Streaming",
			capabilityTools: "Tools",
			capabilityVision: "Vision",
			capabilityWebSearch: "Web Search",
			context: "Context",
			estimatedPerImage: "Est. per Image",
			expandDetails: "Expand details",
			familyLabel: "{family} family",
			hideTokenPricing: "Hide token pricing",
			imageInput: "Image Input",
			imagePricing: "Image Pricing",
			imageOutput: "Image Output",
			input: "Input",
			maxOutput: "Max Output",
			notAvailable: "N/A",
			output: "Output",
			perRequest: "Per Request",
			perSecond: "Per Second",
			removeFromFavorites: "Remove from favorites",
			searchModels: "Search models...",
			searchSelectModel: "Search and select a model...",
			rootModelDescription:
				"This is a root model ID. The gateway will automatically select the best provider for this model based on availability, performance, and cost. Specific capabilities and pricing will depend on the selected provider.",
			selectingModelOpensVideoStudio:
				"Selecting this model will open Video Studio.",
			selectAudioModel: "Select an audio model...",
			selectImageModel: "Select an image model...",
			selectModel: "Select model...",
			selectVideoModel: "Select a video model...",
			showFavoritesOnly: "Show favorites only",
			showOnlyRoot: "Show only root models",
			showOnlyWithKeys: "Show only providers with keys",
			startsAt: "starts at",
			typicalImageOutput: "Typical 1024x1024 output",
			upTo: "Up to",
			videoPricing: "Video Pricing",
		},
		playground: {
			audioStudio: "Audio Studio",
			canvas: "Canvas",
			chat: "Chat",
			chats: "Chats",
			comparisonMode: "Comparison mode",
			dashboard: "Dashboard",
			groupChat: "Group Chat",
			hundredsMore: "+ hundreds more",
			audioHistoryDescription: "Generate audio to see it here",
			imageStudio: "Image Studio",
			imageHistoryDescription: "Generate an image to see it here",
			imageSignInDescription: "Please sign in to generate images.",
			mockAssistant:
				"Auto-routing picked the best one for the job. Want me to compare a few side by side?",
			mockUser: "Which model should I use for this?",
			last7Days: "Last 7 days",
			loadingChats: "Loading chats...",
			logOut: "Log out",
			messagesCount: "{count} messages",
			newCanvas: "New Canvas",
			newChat: "New Chat",
			newGeneration: "New Generation",
			newSkill: "New Skill",
			noChatHistory: "No chat history",
			noChatHistoryDescription: "Start a new conversation to see it here",
			noGenerationHistory: "No generation history",
			noSharedChats: "No shared chats",
			noSharedChatsDescription:
				"Share a chat with this organization to see it here",
			noSkillsYet: "No skills yet",
			older: "Older",
			pinned: "Pinned",
			pin: "Pin",
			rename: "Rename",
			searchChats: "Search Chats",
			signInRequired: "Sign in required",
			signInRequiredDescription:
				"Please sign in to view organizations, projects, and chats.",
			audioSignInDescription: "Please sign in to generate audio.",
			canvasSignInDescription: "Please sign in to use Canvas.",
			orgSharesSignInDescription:
				"Please sign in to view organizations and shared chats.",
			skillsSignInDescription: "Please sign in to view your skills.",
			videoSignInDescription: "Please sign in to generate videos.",
			videoHistoryDescription: "Generate a video to see it here",
			temporaryChat: "Temporary Chat",
			temporaryChatOn: "Temporary chat is on",
			theme: "Theme",
			today: "Today",
			toggleTemporaryChat: "Toggle temporary chat",
			unpin: "Unpin",
			user: "User",
			videoStudio: "Video Studio",
			yesterday: "Yesterday",
		},
		search: {
			closeSearch: "Close search",
			loadingMoreChats: "Loading more chats...",
			loadingSharedChats: "Loading shared chats...",
			noChatsYet: "No chats yet",
			noMatchingChats: "No matching chats",
			noSharedChatsInOrganization: "No shared chats in this organization",
			organizationChatsDescription:
				"Search through shared chats in this organization.",
			organizationChatsTitle: "Search organization chats",
			searchChatsDescription:
				"Search through your past chats or start a new one.",
			searchChatsPlaceholder: "Search chats...",
			searchingChats: "Searching chats...",
			searchSharedChatsPlaceholder: "Search shared chats...",
		},
		share: {
			add: "Add",
			addOrganization: "Add organization",
			alreadyShared: "Already shared",
			copyLink: "Copy link",
			copied: "Copied",
			createShareLink: "Create share link",
			deleteSharedLink: "Delete shared link",
			failedCopyShareLink: "Failed to copy share link",
			linkCopied: "Link copied",
			organization: "Organization",
			organizationDescription: "Members of an organization can view",
			organizationInfo:
				"Only messages up to this point will be shared. Members of the selected organization can view the snapshot and fork it to continue privately.",
			public: "Public",
			publicCreateDescription:
				"Only messages up to this point will be shared. Anyone with the link will be able to see the snapshot.",
			publicDescription: "Anyone with the link can view",
			publicInfo:
				"Anyone with this link can open the snapshot. Avoid sharing private details, and remove the link when it should no longer be available.",
			publicPrivacyInfo:
				"Avoid sharing private details - once the link is created you can copy it and share it on the copied link.",
			removeOrganization: "Remove {name}",
			selectOrganization: "Select organization",
			shareActive: "Share active",
			shareAfterResponse: "Share is available after the response finishes",
			shareChat: "Share chat",
			sharedChat: "Shared chat",
			sharedWithOrganization: "Shared with organization",
			snapshotVisible:
				"A snapshot of this chat will be visible to anyone with the link.",
		},
		time: {
			hoursAgo: "{count}h ago",
			justNow: "Just now",
			yesterday: "Yesterday",
		},
		studio: {
			addImage: "Add image",
			aspectRatio: "Aspect ratio",
			auto: "Auto",
			audio: "Audio",
			audioFixedByModel: "Audio output is fixed by each selected model",
			audioAlwaysOnForModel:
				"The selected model always generates video with audio",
			audioSilentOnlyForModel: "The selected model only generates silent video",
			audioEmptyDescription:
				"Enter the text you want to hear and click Generate to get started.",
			audioRef: "Audio ref",
			count: "Count",
			describeImage: "Describe the image you want to generate...",
			describeImageEdit:
				"Describe how to edit the image... (paste or drop an image)",
			describeImageWithOptionalInput:
				"Describe the image you want to generate... (optionally paste or drop an image)",
			describeVideo:
				"Describe the video you want to generate... (optionally paste or drop a first/last frame or reference image)",
			dropImageHere: "Drop image here",
			duration: "Duration",
			durationSeconds: "{count} seconds",
			expiresIn: "Expires in {time}",
			firstFrame: "First frame",
			format: "Format",
			frameInputUnsupported: "Frame input not supported by selected model",
			imageGenerationFailed: "Image generation failed",
			imageInputUnsupported: "Image input not supported by selected model",
			imageRequiredForModel:
				"This model requires an image input. Please add an image before generating.",
			imageCount: "{count} images",
			image: "Image",
			imageEmptyDescription:
				"Describe what you want to create and click Generate to get started.",
			imageSize: "Image Size",
			inputImage: "Input {count}",
			insertPrompt: "Insert prompt",
			lastFrame: "Last frame",
			missingStartFrame: "This model requires a start frame image",
			quality: "Quality",
			reference: "Reference",
			referenceAudio: "Reference audio {count}",
			referenceAudioCount: "{count}/3 audio",
			referenceAudioUrl: "Reference audio URL (https://)",
			referenceImage: "Reference {count}",
			referenceImageCount: "{count}/3 refs",
			referenceImagesUnsupported:
				"Reference images not supported by selected model",
			referenceVideo: "Reference video {count}",
			referenceVideoCount: "{count}/3 videos",
			referenceVideoUrl: "Reference video URL (https://)",
			selectedModelRequiresStartFrame:
				"Selected model requires an input image. Please add a start frame.",
			removeAttachment: "Remove attachment",
			removeReferenceAudio: "Remove reference audio",
			removeReferenceImage: "Remove reference image",
			removeReferenceVideo: "Remove reference video",
			replaceFirst: "Replace first",
			replaceLast: "Replace last",
			resolution: "Resolution",
			speed: "Speed",
			textToSpeechPrompt: "Enter the text you want to turn into speech...",
			textToSpeechStyle:
				"Optional style instructions, e.g. 'Speak in a warm, friendly tone'",
			downloadAudio: "Download audio",
			downloadImage: "Download image",
			downloadModelAudio: "Download {model} audio",
			downloadVideo: "Download video",
			generatedAudio: "Generated audio",
			generatedImage: "Generated image",
			generatedImageThumbnail: "Generated image thumbnail",
			generatingPercent: "Generating... {progress}%",
			hoursShort: "{count} h",
			lessThanMinute: "less than a minute",
			minutesShort: "{count} min",
			noAudioGenerated:
				"The model did not generate any audio. Try a different model.",
			noImagesGenerated:
				"The model did not generate any images. Try a different model.",
			noAudioYet: "No audio yet",
			noImagesYet: "No images yet",
			noVideosYet: "No videos yet",
			lowCreditsVideo:
				"Low credits remaining. Top up to continue generating videos.",
			outOfCreditsVideo:
				"You're out of credits. Upgrade to a plan to continue generating videos.",
			queued: "Queued...",
			audioGenerationFailed: "Audio generation failed",
			useAsImageReference: "Use as image reference",
			video: "Video",
			videoEmptyDescription:
				"Describe the video you want to create and click Generate to get started.",
			videoExpired: "Video expired",
			videoUnavailable: "Video unavailable",
			videoUnavailableDescription:
				"The video may no longer be available from the provider",
			videoGenerationFailed: "Video generation failed",
			viewPlans: "View plans",
			voice: "Voice",
		},
		canvas: {
			apply: "Apply",
			describeUi: "Describe the UI you want to build...",
			emptyDescription:
				"Describe the UI you want to build and watch it render live, or pick a template above.",
			export: "Export",
			exportAsPdf: "Export as PDF",
			exportAsPng: "Export as PNG",
			exportFailed: "Failed to export {format}",
			generationFailed: "Generation failed",
			hideJson: "Hide JSON",
			invalidJson: "Invalid JSON",
			invalidSpecResponse: "Failed to parse AI response as a valid spec",
			jsonSpec: "JSON Spec",
			noResponseStream: "No response stream",
			noCanvasYet: "No canvas yet",
			pdfExported: "PDF exported",
			pngExported: "PNG exported",
			preview: "Preview",
			previewNotAvailable: "Preview not available",
			resetCanvas: "Reset canvas",
			resetCanvasDescription:
				"Your current canvas has content. Starting a new canvas will clear everything. This cannot be undone.",
			resetCanvasTitle: "Reset canvas?",
			showJson: "Show JSON",
			specMissingFields: "Spec must have 'root' and 'elements' fields",
			templates: "Templates",
		},
		groupChat: {
			addAtLeastTwoModels:
				"Add at least 2 models to start a group conversation",
			answerDescription:
				"Send the same prompt to multiple AI models at once and compare the answers side by side.",
			answerTitle: "Answer Mode",
			awaitingResponse: "Waiting for this model to reply",
			clearConversation: "Clear",
			continueConversation: "Continue Conversation",
			debateDescription:
				"Let selected AI models debate in sequence and inspect the turn order across columns.",
			debateTitle: "Debate Mode",
			howItWorks: "How it works",
			initialPrompt: "Initial Prompt",
			instructionsAddModels: "Add 2-5 different AI models to the conversation",
			instructionsAnswerInParallel:
				"Send one prompt to every selected model at the same time",
			instructionsBuildOnResponses:
				"Each model builds on the previous responses, creating a dynamic conversation",
			instructionsCompareSideBySide:
				"Compare the answers side by side in the current multi-column layout",
			instructionsEnterPrompt:
				"Enter your initial prompt or question to kick off the discussion",
			instructionsStopAnytime:
				"You can stop the conversation at any time and start a new one",
			instructionsTakeTurns:
				"Models will take turns responding to each other in sequence",
			missingPromptOrModels:
				"Please enter a prompt and select at least 2 models",
			modeAnswer: "Answer",
			modeDebate: "Debate",
			modelReplyCount: "Reply {count}",
			removeModel: "Remove {model}",
			rerunAnswers: "Run Again",
			sequenceOrder: "Turn {order}",
			selectAtLeastTwoModels:
				"Please select at least 2 models to start a group conversation",
			selectedModels: "Selected Models ({count}/5)",
			startAnswering: "Start Answering",
			startDebating: "Start Debate",
			startConversation: "Start Conversation",
			stopConversation: "Stop Conversation",
			title: "Group Chat Mode",
			topicPlaceholder:
				"Enter a topic or question for the models to discuss... (e.g., 'What are the pros and cons of functional programming?')",
			unknownModel: "Unknown",
		},
		chatUi: {
			activityLog: "Activity log",
			addAttachments: "Add {items}",
			addAttachmentsOr: "Add {items} or {last}",
			audioAttachment: "Audio attachment",
			auto: "Auto",
			cachedInputTokens: "Cached input tokens",
			clickPlusToCreateSkill: "No skills yet. Click + to create one.",
			copiedToClipboard: "Copied to clipboard",
			copy: "Copy",
			copyToClipboard: "Copy to clipboard",
			create: "Create",
			createSkill: "Create skill",
			discount: "Discount",
			document: "Document",
			documents: "documents",
			editAndRetry: "Edit and retry",
			editAndRetryFromHere: "Edit and retry from here",
			failedToCopyToClipboard: "Failed to copy to clipboard",
			forkChat: "Fork chat",
			generationStopped: "Generation stopped: {reason}",
			generatedImage: "Generated image",
			howCanIHelp: "How can I help you?",
			inputTokens: "Input tokens",
			messagePlaceholder: "Message",
			minimal: "Minimal",
			noSkillsYetCreate: "No skills yet. Click + to create one.",
			outputTokens: "Output tokens",
			photos: "photos",
			reasoning: "Reasoning",
			reasoningHigh: "High",
			reasoningLow: "Low",
			reasoningMedium: "Medium",
			regenerateResponse: "Regenerate response",
			removeSkill: "Remove skill",
			responseContentFiltered: "Response was filtered by content policy",
			responseReachedMaxTokens: "Response reached the maximum token limit",
			responseMetadata: "Response metadata",
			retry: "Retry",
			selectSkill: "Select a skill",
			send: "Send",
			suggestionCode: "Code",
			suggestionCreate: "Create",
			suggestionExplore: "Explore",
			suggestionImageGen: "Image gen",
			temporaryChatSubtitle:
				"Temporary chats will not appear in your chat history.",
			totalCost: "Total cost",
			uploadedImage: "Uploaded image",
			usedModel: "Used model",
			viewAllSkills: "View all skills",
		},
		chatPage: {
			addModelForComparison: "Add model for comparison",
			cannotCreateComparisonChat:
				"Cannot create comparison chat before the primary chat exists",
			chatDeleted: "Chat was deleted. Please start a new conversation.",
			chatForked: "Chat forked",
			chatContextInsufficientCredits:
				"The current chat is billed to the built-in Chat organization, and that organization has no remaining credits. Top up the Chat organization, or switch to a funded organization and start a new chat.",
			chatContextInsufficientCreditsWithOrganization:
				'The current chat is still billed to the built-in Chat organization, and that organization has no remaining credits. Your funded organization "{organization}" has credits, but this chat will not use them automatically. Switch to "{organization}" and start a new chat, or top up the Chat organization.',
			chatWindows: "Chat windows",
			failedCleanupChat: "Failed to cleanup chat: {error}",
			failedCreateChat: "Failed to create a new chat. Please try again.",
			failedParseAudios: "Failed to parse audios: {error}",
			failedParseDocuments: "Failed to parse documents: {error}",
			failedParseImages: "Failed to parse images: {error}",
			failedParseSources: "Failed to parse sources: {error}",
			failedParseTools: "Failed to parse tools: {error}",
			failedSaveAiResponse: "Failed to save AI response: {error}",
			failedSaveAiResponseNoChat:
				"Failed to save AI response: No chat ID found (chat may not have finished saving before the stream ended).",
			forkChat: "Fork chat",
			forking: "Forking...",
			modelLabel: "Model {index}",
			noChatSelected: "No chat selected.",
			organizationInsufficientCredits:
				'The current organization "{organization}" has no remaining credits. Top up this organization or switch to another funded organization.',
			providerLabelAlibaba: "Alibaba Bailian",
			providerAuthorizationFailed:
				"The platform-level credentials for {provider} are invalid, missing, or no longer authorized. Check the provider key and permission configuration, then try again.",
			providerAuthorizationFailedGeneric:
				"The platform-level provider credentials are invalid, missing, or no longer authorized. Check the provider key and permission configuration, then try again.",
			openAudioStudio: "Open Audio Studio",
			openVideoStudio: "Open Video Studio",
			removeWindow: "Remove window",
			selectModel: "Select a model...",
			switchToAudioDescription:
				"{model} is a speech generation model. Would you like to open it in Audio Studio?",
			switchToAudioTitle: "Switch to Audio Studio?",
			switchToVideoDescription:
				"{model} is a video generation model. Would you like to open it in Video Studio?",
			switchToVideoTitle: "Switch to Video Studio?",
			syncPromptInput: "Sync prompt input",
			toggleOff: "Off",
			toggleOn: "On",
		},
		apiKey: {
			apiKey: "API Key",
			apiKeyCopied: "API Key Copied",
			apiKeyCopiedDescription: "The API key has been copied to your clipboard.",
			apiKeyCreated: "API Key Created",
			apiKeyCreatedDescription:
				"Your API key has been created and applied. Please copy it now as you won't be able to see it again.",
			apiKeyCleared: "API Key Cleared",
			apiKeyClearedDescription: "Your API key has been cleared.",
			apiKeyName: "API Key Name",
			apiKeyNamePlaceholder: "e.g. Playground Test Key",
			apiKeyRequired: "API key is required",
			apiKeySaved: "API Key Saved",
			apiKeySavedDescription:
				"Your API key has been saved and activated successfully!",
			apiKeySavedNewDescription:
				"Your new API key has been saved and activated!",
			clearKey: "Clear Key",
			copyApiKey: "Copy API key",
			createApiKey: "Create API Key",
			editKey: "Edit Key",
			enterApiKey: "Enter Your API Key",
			enterApiKeyDescription:
				"To use the playground, you need to provide your own doteb API key.",
			enterApiKeyPlaceholder: "Enter your doteb API key...",
			invalidApiKey: "Invalid API Key",
			invalidApiKeyDescription: "Please check your API key and try again.",
			invalidApiKeyShort:
				"Invalid API key. Please check your key and try again.",
			keyRequiredCheck:
				"API key is required. Please check your key and try again.",
			localStorageDescription:
				"Your API key is stored locally in your browser and is only used to make requests to the doteb API on your behalf.",
			manageApiKey: "Manage Your API Key",
			manageApiKeyDescription: "View, edit, or clear your saved API key.",
			pasteExistingKey: "Paste Existing Key",
			createNewKey: "Create New Key",
			saveApiKey: "Save API Key",
			secureStorageReminder: "Make sure to store this API key securely.",
			useCreditsDescription:
				"Enter your doteb API key to start chatting with AI models using credits.",
			validApiKeyRequired: "Please enter a valid API key",
			validationFailed: "API key validation failed: {status} {statusText}",
			yourApiKey: "Your API Key",
		},
		mcp: {
			activeCount: "MCP Servers ({count} active)",
			addServer: "Add MCP Server",
			apiKeyOptional: "API Key (optional)",
			deleteServer: "Delete server",
			description:
				"Connect to Model Context Protocol servers to extend AI capabilities with external tools.",
			editServer: "Edit server",
			emptyDescription: "Add a server to extend AI capabilities",
			emptyTitle: "No MCP servers configured",
			hideApiKey: "Hide API key",
			name: "Name",
			save: "Save",
			showApiKey: "Show API key",
			title: "MCP Servers",
			url: "URL",
		},
		skills: {
			deleteConfirm:
				'Are you sure you want to delete "{name}"? This action cannot be undone.',
			deleteSkill: "Delete skill",
			deleting: "Deleting...",
			description: "Description",
			descriptionPlaceholder: "Describe when to use this skill...",
			createDescription:
				"Create a reusable instruction set to guide the AI in a specific context.",
			createSkill: "Create",
			descriptionExample:
				"Apply Acme Corp brand guidelines to presentations and documents...",
			editSkill: "Edit skill",
			fileRequirements: "File requirements",
			fileSizeLimit: "File size must not exceed 50 MB",
			generateAndSave: "Generate & save",
			generateCreated: 'Skill "{name}" created',
			generateDescription:
				"Describe what you want the skill to do and doteb will write and save it for you. You can edit it afterwards.",
			generatePromptLabel: "What should it do?",
			generatePromptPlaceholder:
				"A skill that reviews my pull requests for security issues and suggests fixes in a friendly tone...",
			generateTitle: "Generate skill with AI",
			generateWithAi: "Generate with AI",
			instructions: "Instructions",
			instructionsPlaceholder: "Enter your skill instructions in markdown...",
			loadingSkills: "Loading skills...",
			mySkills: "My Skills",
			name: "Name",
			namePlaceholder: "brand guidelines",
			rawView: "Raw view",
			renderedView: "Rendered view",
			saving: "Saving...",
			selectSkill: "Select a skill to view details",
			title: "Skills",
			uploadDropzone: "Drag and drop or click to upload",
			uploadFile: "Upload skill file",
			uploadMarkdownRequirement:
				".md file must contain a `name` field in YAML frontmatter",
			uploadParseError:
				"Could not parse skill. The .md file must contain YAML frontmatter with a `name` field.",
			uploadSkill: "Upload a skill",
			uploadTitle: "Upload skill",
			uploadUnsupportedFile:
				"Only .md files are supported for upload. .zip/.skill support coming soon.",
			uploadZipRequirement: ".zip or .skill file must include a SKILL.md file",
			viewAllSkills: "View all skills",
			writeSkill: "Write skill",
			writeSkillInstructions: "Write skill instructions",
		},
		authDialog: {
			canvasDescription: "Build and preview UIs with live output",
			description:
				"Chat, images, video, canvas and group chat across 210+ models. Free to start - no credit card required.",
			groupChatDescription: "Run several models side by side and compare",
			highlightHistory: "Save & search your full chat history",
			highlightPlans:
				"Pay-as-you-go credits or a flat monthly plan - your call",
			imageDescription: "Generate and edit images from a prompt",
			title: "Every model and studio - in one place",
			videoDescription: "Turn a prompt into short videos",
			audioDescription: "Turn text into natural-sounding speech",
			chatDescription:
				"GPT, Claude, Gemini & 210+ models - switch mid-conversation",
		},
		entities: {
			createOrganization: "Create New Organization",
			createOrganizationDescription:
				"Create a new organization to group your projects.",
			createOrganizationSubmit: "Create Organization",
			createProject: "Create New Project",
			createProjectDescription: "Create a new project in {organization}.",
			createProjectSubmit: "Create Project",
			failedCreateOrganization: "Failed to create organization",
			failedCreateProject: "Failed to create project",
			name: "Name",
			newProject: "New Project",
			noProjectsYet: "No projects yet",
			organizationCreated: "Organization created successfully",
			organizationCreatedDescription: "{name} has been created.",
			organizationLimitReached: "Organization limit reached",
			organizationLimitReachedDescription:
				"You've reached the maximum number of organizations for your plan. Please contact us to upgrade to Enterprise.",
			projectCreated: "Project created successfully",
			projectCreatedDescription: "{name} has been created.",
			projectLimitReached: "Project limit reached",
			projectLimitReachedDescription:
				"You've reached the maximum number of projects for your plan. Please contact us to upgrade to Enterprise.",
			selectedOrganizationFallback: "the selected organization",
			selectProject: "Select Project",
			projectsIn: "Projects in {organization}",
			organizationFallback: "Organization",
			organizations: "Organizations",
			chatPlan: "Chat plan",
		},
	},
	"zh-CN": {
		common: {
			add: "添加",
			back: "返回",
			cancel: "取消",
			clear: "清空",
			close: "关闭",
			continue: "继续",
			creating: "创建中...",
			default: "默认",
			generate: "生成",
			generating: "生成中...",
			loading: "加载中...",
			or: "或",
			processing: "处理中...",
			redirecting: "跳转中...",
			unexpectedError: "发生未知错误，请重试。",
			validating: "验证中...",
		},
		language: {
			short: "中",
		},
		auth: {
			accountCreated: "账号已创建",
			accountCreatedDescription: "请先检查邮箱并完成验证，然后再登录。",
			alreadyHaveAccount: "已有账号？登录",
			alreadyHaveAccountQuestion: "已有账号？",
			createAccount: "创建账号",
			createAccountLoading: "正在创建账号...",
			createAccountSubtitle: "几秒钟内开始使用全球领先模型聊天",
			dontHaveAccount: "还没有账号？注册",
			emailInvalid: "请输入有效邮箱地址",
			loginBrandLine1: "欢迎回来。",
			loginBrandLine2: "继续上次对话。",
			loginBrandSubtitle: "你的聊天、创作工作台和收藏模型都还在原来的位置。",
			loginSuccess: "登录成功",
			nameOptional: "姓名（可选）",
			passkeyFailed: "通行密钥登录失败",
			passwordMin: "密码至少需要 8 个字符",
			passwordRequired: "请输入密码",
			signIn: "登录",
			signInSubtitle: "登录后继续你的聊天",
			signInWithPasskey: "使用通行密钥登录",
			signUpFailed: "注册失败",
			signingIn: "登录中...",
			startFree: "免费开始",
			signupBrandLine1: "所有模型。",
			signupBrandLine2: "一个聊天入口。",
			signupBrandSubtitle:
				"用一个账号与 GPT、Claude、Gemini 和数百个模型聊天，对比回答，并创建图片、视频和音频。",
			socialSignInFailed: "使用 {provider} 登录失败",
		},
		billing: {
			addCreditsDescription: "为组织账号添加额度。下一步确认明细。",
			addNewPaymentMethod: "添加新支付方式",
			amountSummary: "金额：${amount}",
			amountWholeDollar: "金额必须是整数美元。",
			amountUsd: "金额（USD）",
			cardDetails: "银行卡信息",
			chatPlanLeft: "剩余 ${remaining} / ${limit}",
			checkoutFailed: "结账失败",
			checkoutFailedDescription: "创建结账会话失败。",
			confirmPayment: "确认支付",
			couldNotReadCardDetails: "无法读取银行卡信息。",
			credits: "额度",
			creditsAdded: "额度已添加到你的账号。",
			defaultPaymentMethod: "默认",
			calculatingFees: "正在计算费用...",
			cardPaymentsUnavailable: "当前部署未启用银行卡支付。",
			error: "错误",
			errorSavingCard: "保存银行卡失败",
			expires: "有效期 {month}/{year}",
			feeBreakdown: "费用明细",
			internationalCardFee: "国际银行卡手续费（1.5%）",
			internationalCardsFeeNote: "国际银行卡会额外收取 1.5% 处理费。",
			invoiceSettingsNote:
				"发票需要公司/地址信息？请在购买前更新账单设置。支付后我们会自动通过邮件发送发票。",
			loadingPaymentForm: "正在加载支付表单...",
			lowCredits: "额度余额偏低",
			maximumTopUp: "单次最高充值金额为 ${amount}。",
			minMaxTopUp: "最低 ${min}。最高 ${max}。",
			minimumTopUp: "单次最低充值金额为 ${amount}。",
			noCredits: "额度已用完",
			payWithCard: "用银行卡支付",
			payWithAlipay: "使用支付宝支付",
			payWithStripeCheckout: "使用 Stripe Checkout 支付",
			paymentDetails: "支付信息",
			paymentFailed: "支付失败",
			paymentFailedDescription: "处理支付时发生错误。",
			paymentMethodsDescription:
				"选择一个支付方式为账号添加 ${amount} 额度。下一步确认明细。",
			paymentSummary: "支付汇总",
			paymentSuccessful: "支付成功",
			payAmount: "支付 ${amount}",
			platformFee: "平台手续费（5%）",
			reviewPaymentDetails: "确认前请检查支付明细。",
			saveCard: "保存此卡用于后续支付",
			selectPaymentMethod: "选择支付方式",
			stripeCheckoutMethods:
				"Stripe Checkout 支持 Google Pay、Apple Pay 等更多支付方式。",
			thankYouPurchase: "感谢购买。你的额度现在可以使用了。",
			topUpCredits: "充值额度",
			total: "总计",
			upgradeCredits: "最高可获得 3 倍额度",
			upgradeMonthly: "升级月度套餐 · $9/月起",
			useCardDescription: "输入银行卡信息，为账号添加 ${amount} 额度。",
		},
		pricing: {
			backToChat: "返回聊天",
			billingCycleNotice:
				"额度会在每个账单周期重置且不会结转。按量充值额度会继续保留；来自聊天应用的请求会优先消耗聊天套餐额度。",
			bulletCancelBody: "订阅会持续到你已支付周期的结束时间。",
			bulletCancelTitle: "随时取消。",
			bulletExpireBody: "每个周期都会获得新的额度；未使用额度不会结转。",
			bulletExpireTitle: "额度在月底过期。",
			bulletFrontierBody:
				"（Opus、GPT-5、Gemini 2.5 Pro、Grok 4）。Plus 和 Pro 包含全部模型。",
			bulletFrontierTitle: "Starter 不包含前沿模型",
			bulletTopUpBody: "充值额度永不过期，会在月度额度用完后作为备用余额。",
			bulletTopUpTitle: "你的按量充值余额会保留。",
			cancelPlan: "取消套餐",
			cancelledSuccess: "套餐已取消，访问权限会持续到当前周期结束",
			cancellationFailed: "取消失败",
			changeFailed: "切换失败",
			changeUnavailable: "暂不支持切换套餐",
			changeUnavailableHint:
				"当前聊天套餐通过支付宝计费。如需切换档位，请先在周期结束时取消，再订阅新的档位。",
			confirmCancel: "取消聊天套餐？当前周期结束前你仍可继续使用。",
			currentPeriod: " · {action} {date}",
			currentPlan: "当前套餐",
			currentPlanCta: "当前套餐",
			currentUsage: "已使用 ${used} / ${limit}",
			getPlan: "开通 {plan}",
			headline: "三档套餐。Plus 和 Pro 可用前沿模型。",
			heroPrefix: "订阅后可获得",
			heroStrong: "最高 3 倍的美元额度",
			heroSuffix: "。额度每个周期重置；按量充值余额仍会同时保留。",
			howItWorks: "工作方式",
			invoiceInfo:
				"发票需要公司/地址信息？请在购买前更新账单设置。支付后我们会自动通过邮件发送发票。",
			metadataDescription:
				"订阅聊天套餐，最高可获得 3 倍美元额度。所有前沿模型集中在一个入口，按月计费。",
			metadataTitle: "价格 - doteb Chat",
			mostPopular: "最受欢迎",
			paidShort: "已支付 ${amount}",
			perMonth: "/月",
			periodEnds: "到期",
			periodRenews: "续订",
			plans: {
				starter: {
					name: "Starter",
					description: "适合偶尔、轻量聊天使用",
					tagline: "体验每类模型",
					featureModels:
						"多数聊天模型 - Claude Haiku、Sonnet、GPT-5-mini、Gemini Flash 等",
					featureChat: "在 chat.doteb.com 使用网页版聊天",
					featureUsage: "实时用量和单条消息成本",
					featureSwitch: "可随时切换档位，按比例结算",
				},
				plus: {
					name: "Plus",
					description: "略高于 ChatGPT Plus，但包含所有前沿模型",
					tagline: "日常默认选择",
					featureStarter: "包含 Starter 全部内容，并增加前沿模型",
					featureModels: "Claude Opus、GPT-5、Gemini 2.5 Pro、Grok 4 全部包含",
					featureHeadroom: "满足较长的日常聊天会话",
					featureSupport: "邮件支持",
				},
				pro: {
					name: "Pro",
					description: "适合全天高频聊天的重度用户",
					tagline: "最高用量，最佳单位价值",
					featurePlus: "包含 Plus 全部内容",
					featureAllowance: "最大的月度额度，3 倍价值",
					featureSupport: "优先支持",
					featureValue: "高用量下价值最高",
				},
			},
			providerRates: "按供应商价格使用",
			redirecting: "跳转中...",
			resume: "恢复",
			resumeFailed: "恢复失败",
			resumedSuccess: "套餐已恢复",
			subscribeFailed: "订阅失败",
			switchedTo: "已切换到 {plan}",
			switching: "切换中...",
			switchToPlan: "切换到 {plan}",
			usedShort: "已使用 ${amount}",
			whatYouGet: "你实际获得",
			multiplierValue: "{multiplier} 倍价值",
			youPay: "你支付",
			youUse: "你可用",
			yourPlan: "你的套餐",
		},
		aiElements: {
			addPhotosOrFiles: "添加图片或文件",
			attachmentAlt: "附件",
			branchPage: "{current} / {total}",
			cache: "缓存",
			console: "控制台",
			context: "上下文",
			copyModelId: "复制模型 ID",
			error: "错误",
			free: "免费",
			generatedImage: "生成的图片 {index}",
			imageModelsFound: "找到 {count} 个图片模型",
			input: "输入",
			inputShort: "输入",
			messagePlaceholder: "想了解什么？",
			modelContextUsage: "模型上下文用量",
			modelsFound: "找到 {count} 个模型",
			next: "下一个",
			nextBranch: "下一条分支",
			noAcceptedFiles: "没有文件符合允许的类型。",
			noConsoleOutput: "暂无控制台输出",
			output: "输出",
			outputShort: "输出",
			parameters: "参数",
			perRequest: "/ 次请求",
			previous: "上一个",
			previousBranch: "上一条分支",
			price: "价格",
			pricing: "价格",
			providers: "供应商",
			reasoning: "推理",
			removeAttachment: "移除附件",
			result: "结果",
			statusApprovalRequested: "请求审批",
			statusApprovalResponded: "审批已响应",
			statusCompleted: "已完成",
			statusDenied: "已拒绝",
			statusPending: "等待中",
			statusRunning: "运行中",
			submit: "提交",
			thoughtFewSeconds: "思考了几秒",
			thoughtSeconds: "思考了 {duration} 秒",
			thinking: "思考中...",
			tooManyFiles: "文件过多，部分文件未添加。",
			totalCost: "总费用",
			unknown: "未知",
			unknownFile: "未知文件",
			uploadFiles: "上传文件",
			urlPlaceholder: "输入 URL...",
			webPreviewTitle: "预览",
			filesExceedMaxSize: "所有文件都超过大小限制。",
		},
		modelSelector: {
			allModels: "全部模型",
			autoSelectProvider: "自动选择供应商",
			capabilities: "能力",
			clearAll: "全部清除",
			clearFilters: "清除筛选以查看全部模型",
			filters: "筛选",
			freeModels: "免费模型",
			from: "从",
			hideUnstable: "隐藏不稳定/实验模型",
			highCost: "高成本（> $0.00001）",
			lowCost: "低成本（≤ $0.000001）",
			mediumCost: "中等成本（≤ $0.00001）",
			modelDetails: "模型详情",
			modelsFound: "找到 {count} 个模型",
			noModelsFound: "未找到模型。",
			priceRange: "价格范围",
			pricingLimits: "价格与限制",
			providers: "供应商",
			addModel: "添加模型...",
			addToFavorites: "添加到收藏",
			cachedInput: "缓存输入",
			capabilityImageGeneration: "图片生成",
			capabilityJsonOutput: "JSON 输出",
			capabilityReasoning: "推理",
			capabilityStreaming: "流式输出",
			capabilityTools: "工具调用",
			capabilityVision: "视觉",
			capabilityWebSearch: "联网搜索",
			context: "上下文",
			estimatedPerImage: "预估每张图片",
			expandDetails: "展开详情",
			familyLabel: "{family} 系列",
			hideTokenPricing: "隐藏 token 价格",
			imageInput: "图片输入",
			imagePricing: "图片价格",
			imageOutput: "图片输出",
			input: "输入",
			maxOutput: "最大输出",
			notAvailable: "不适用",
			output: "输出",
			perRequest: "每次请求",
			perSecond: "每秒",
			removeFromFavorites: "从收藏中移除",
			searchModels: "搜索模型...",
			searchSelectModel: "搜索并选择模型...",
			rootModelDescription:
				"这是根模型 ID。网关会根据可用性、性能和成本自动为该模型选择最佳供应商。具体能力和价格会取决于最终选中的供应商。",
			selectingModelOpensVideoStudio: "选择此模型会打开视频工作台。",
			selectAudioModel: "选择音频模型...",
			selectImageModel: "选择图片模型...",
			selectModel: "选择模型...",
			selectVideoModel: "选择视频模型...",
			showFavoritesOnly: "只显示收藏",
			showOnlyRoot: "只显示根模型",
			showOnlyWithKeys: "只显示已配置密钥的供应商",
			startsAt: "起价",
			typicalImageOutput: "典型 1024x1024 输出",
			upTo: "最高",
			videoPricing: "视频价格",
		},
		playground: {
			audioStudio: "音频工作台",
			canvas: "画布",
			chat: "聊天",
			chats: "聊天记录",
			comparisonMode: "对比模式",
			dashboard: "控制台",
			groupChat: "群聊对比",
			hundredsMore: "+ 数百个模型",
			audioHistoryDescription: "生成音频后会显示在这里",
			imageStudio: "图片工作台",
			imageHistoryDescription: "生成图片后会显示在这里",
			imageSignInDescription: "请先登录以生成图片。",
			mockAssistant:
				"自动路由已为这个任务选择了合适模型。要并排比较几个模型吗？",
			mockUser: "这个任务应该用哪个模型？",
			last7Days: "最近 7 天",
			loadingChats: "正在加载聊天...",
			logOut: "退出登录",
			messagesCount: "{count} 条消息",
			newCanvas: "新画布",
			newChat: "新聊天",
			newGeneration: "新生成",
			newSkill: "新技能",
			noChatHistory: "暂无聊天记录",
			noChatHistoryDescription: "开始新对话后会显示在这里",
			noGenerationHistory: "暂无生成历史",
			noSharedChats: "暂无共享聊天",
			noSharedChatsDescription: "将聊天共享到此组织后会显示在这里",
			noSkillsYet: "暂无技能",
			older: "更早",
			pinned: "已置顶",
			pin: "置顶",
			rename: "重命名",
			searchChats: "搜索聊天",
			signInRequired: "需要登录",
			signInRequiredDescription: "请先登录以查看组织、项目和聊天记录。",
			audioSignInDescription: "请先登录以生成音频。",
			canvasSignInDescription: "请先登录以使用画布。",
			orgSharesSignInDescription: "请先登录以查看组织和共享聊天。",
			skillsSignInDescription: "请先登录以查看你的技能。",
			videoSignInDescription: "请先登录以生成视频。",
			videoHistoryDescription: "生成视频后会显示在这里",
			temporaryChat: "临时聊天",
			temporaryChatOn: "临时聊天已开启",
			theme: "主题",
			today: "今天",
			toggleTemporaryChat: "切换临时聊天",
			unpin: "取消置顶",
			user: "用户",
			videoStudio: "视频工作台",
			yesterday: "昨天",
		},
		search: {
			closeSearch: "关闭搜索",
			loadingMoreChats: "正在加载更多聊天...",
			loadingSharedChats: "正在加载共享聊天...",
			noChatsYet: "暂无聊天",
			noMatchingChats: "没有匹配的聊天",
			noSharedChatsInOrganization: "此组织暂无共享聊天",
			organizationChatsDescription: "搜索此组织中的共享聊天。",
			organizationChatsTitle: "搜索组织聊天",
			searchChatsDescription: "搜索历史聊天，或开始一个新聊天。",
			searchChatsPlaceholder: "搜索聊天...",
			searchingChats: "正在搜索聊天...",
			searchSharedChatsPlaceholder: "搜索共享聊天...",
		},
		share: {
			add: "添加",
			addOrganization: "添加组织",
			alreadyShared: "已共享",
			copyLink: "复制链接",
			copied: "已复制",
			createShareLink: "创建共享链接",
			deleteSharedLink: "删除共享链接",
			failedCopyShareLink: "复制共享链接失败",
			linkCopied: "链接已复制",
			organization: "组织",
			organizationDescription: "组织成员可查看",
			organizationInfo:
				"只会共享截至当前的消息。所选组织的成员可以查看快照，并 fork 后私下继续。",
			public: "公开",
			publicCreateDescription:
				"只会共享截至当前的消息。拥有链接的任何人都可以查看此快照。",
			publicDescription: "拥有链接的任何人都可查看",
			publicInfo:
				"拥有此链接的人可以打开快照。请避免共享隐私信息，不再需要时请移除链接。",
			publicPrivacyInfo:
				"请避免共享隐私信息。链接创建后，你可以复制并分享到 复制后的链接。",
			removeOrganization: "移除 {name}",
			selectOrganization: "选择组织",
			shareActive: "共享已开启",
			shareAfterResponse: "回答完成后才能共享",
			shareChat: "共享聊天",
			sharedChat: "共享聊天",
			sharedWithOrganization: "已共享给组织",
			snapshotVisible: "此聊天快照将对拥有链接的任何人可见。",
		},
		time: {
			hoursAgo: "{count} 小时前",
			justNow: "刚刚",
			yesterday: "昨天",
		},
		studio: {
			addImage: "添加图片",
			aspectRatio: "宽高比",
			auto: "自动",
			audio: "音频",
			audioFixedByModel: "音频输出由各个所选模型固定",
			audioAlwaysOnForModel: "所选模型始终生成带音频的视频",
			audioSilentOnlyForModel: "所选模型只能生成无声视频",
			audioEmptyDescription: "输入你想听到的文本，然后点击生成开始。",
			audioRef: "音频参考",
			count: "数量",
			describeImage: "描述你想生成的图片...",
			describeImageEdit: "描述如何编辑图片...（可粘贴或拖入图片）",
			describeImageWithOptionalInput:
				"描述你想生成的图片...（可选粘贴或拖入图片）",
			describeVideo:
				"描述你想生成的视频...（可选粘贴或拖入首帧、尾帧或参考图）",
			dropImageHere: "把图片拖到这里",
			duration: "时长",
			durationSeconds: "{count} 秒",
			expiresIn: "{time} 后过期",
			firstFrame: "首帧",
			format: "格式",
			frameInputUnsupported: "所选模型不支持帧图片输入",
			imageGenerationFailed: "图片生成失败",
			imageInputUnsupported: "所选模型不支持图片输入",
			imageRequiredForModel: "此模型需要输入图片。请先添加图片再生成。",
			imageCount: "{count} 张图片",
			image: "图片",
			imageEmptyDescription: "描述你想创建的内容，然后点击生成开始。",
			imageSize: "图片尺寸",
			inputImage: "输入图片 {count}",
			insertPrompt: "插入提示词",
			lastFrame: "尾帧",
			missingStartFrame: "此模型需要首帧图片",
			quality: "质量",
			reference: "参考",
			referenceAudio: "参考音频 {count}",
			referenceAudioCount: "{count}/3 个音频",
			referenceAudioUrl: "参考音频 URL（https://）",
			referenceImage: "参考 {count}",
			referenceImageCount: "{count}/3 张参考图",
			referenceImagesUnsupported: "所选模型不支持参考图",
			referenceVideo: "参考视频 {count}",
			referenceVideoCount: "{count}/3 个视频",
			referenceVideoUrl: "参考视频 URL（https://）",
			selectedModelRequiresStartFrame:
				"所选模型需要输入图片。请添加首帧后再生成。",
			removeAttachment: "移除附件",
			removeReferenceAudio: "移除参考音频",
			removeReferenceImage: "移除参考图",
			removeReferenceVideo: "移除参考视频",
			replaceFirst: "替换首帧",
			replaceLast: "替换尾帧",
			resolution: "分辨率",
			speed: "速度",
			textToSpeechPrompt: "输入要转成语音的文本...",
			textToSpeechStyle: "可选风格说明，例如“用温暖、友好的语气朗读”",
			downloadAudio: "下载音频",
			downloadImage: "下载图片",
			downloadModelAudio: "下载 {model} 音频",
			downloadVideo: "下载视频",
			generatedAudio: "生成的音频",
			generatedImage: "生成的图片",
			generatedImageThumbnail: "生成图片缩略图",
			generatingPercent: "生成中... {progress}%",
			hoursShort: "{count} 小时",
			lessThanMinute: "不到 1 分钟",
			minutesShort: "{count} 分钟",
			noAudioGenerated: "该模型未生成任何音频。请尝试其他模型。",
			noImagesGenerated: "该模型未生成任何图片。请尝试其他模型。",
			noAudioYet: "暂无音频",
			noImagesYet: "暂无图片",
			noVideosYet: "暂无视频",
			lowCreditsVideo: "额度余额偏低。请充值后继续生成视频。",
			outOfCreditsVideo: "额度已用完。请升级套餐以继续生成视频。",
			queued: "排队中...",
			audioGenerationFailed: "音频生成失败",
			useAsImageReference: "用作图片参考",
			video: "视频",
			videoEmptyDescription: "描述你想创建的视频，然后点击生成开始。",
			videoExpired: "视频已过期",
			videoUnavailable: "视频不可用",
			videoUnavailableDescription: "该视频可能已无法从供应商处访问",
			videoGenerationFailed: "视频生成失败",
			viewPlans: "查看套餐",
			voice: "声音",
		},
		canvas: {
			apply: "应用",
			describeUi: "描述你想构建的 UI...",
			emptyDescription:
				"描述你想构建的 UI 并实时查看渲染结果，或从上方选择模板。",
			export: "导出",
			exportAsPdf: "导出为 PDF",
			exportAsPng: "导出为 PNG",
			exportFailed: "导出 {format} 失败",
			generationFailed: "生成失败",
			hideJson: "隐藏 JSON",
			invalidJson: "JSON 无效",
			invalidSpecResponse: "无法将 AI 响应解析为有效规格",
			jsonSpec: "JSON 规格",
			noResponseStream: "没有响应流",
			noCanvasYet: "暂无画布",
			pdfExported: "PDF 已导出",
			pngExported: "PNG 已导出",
			preview: "预览",
			previewNotAvailable: "预览不可用",
			resetCanvas: "重置画布",
			resetCanvasDescription:
				"当前画布已有内容。开始新画布会清空所有内容，且无法撤销。",
			resetCanvasTitle: "重置画布？",
			showJson: "显示 JSON",
			specMissingFields: "规格必须包含 'root' 和 'elements' 字段",
			templates: "模板",
		},
		groupChat: {
			addAtLeastTwoModels: "至少添加 2 个模型后才能开始群聊对比",
			answerDescription: "把同一个问题同时发给多个模型，按列直接对比各自答案。",
			answerTitle: "作答模式",
			awaitingResponse: "等待该模型回复",
			clearConversation: "清空",
			continueConversation: "继续对话",
			debateDescription: "让多个模型按顺序辩论，并按列清晰展示每一轮发言位置。",
			debateTitle: "辩论模式",
			howItWorks: "工作方式",
			initialPrompt: "初始提示词",
			instructionsAddModels: "向对话中添加 2-5 个不同 AI 模型",
			instructionsAnswerInParallel: "同一个提示词会同时发给所有已选模型",
			instructionsBuildOnResponses:
				"每个模型都会基于前一条回答继续展开，形成动态对话",
			instructionsCompareSideBySide: "使用当前多列布局直接横向比较各模型答案",
			instructionsEnterPrompt: "输入初始提示词或问题来开启讨论",
			instructionsStopAnytime: "你可以随时停止对话并开始新的对话",
			instructionsTakeTurns: "模型会按顺序轮流回应彼此",
			missingPromptOrModels: "请输入提示词并至少选择 2 个模型",
			modeAnswer: "作答",
			modeDebate: "辩论",
			modelReplyCount: "本模型第 {count} 次回复",
			removeModel: "移除 {model}",
			rerunAnswers: "重新作答",
			sequenceOrder: "顺序 {order}",
			selectAtLeastTwoModels: "请至少选择 2 个模型来开始群聊对比",
			selectedModels: "已选模型（{count}/5）",
			startAnswering: "开始作答",
			startDebating: "开始辩论",
			startConversation: "开始对话",
			stopConversation: "停止对话",
			title: "群聊对比模式",
			topicPlaceholder:
				"输入模型要讨论的话题或问题...（例如：“函数式编程的优缺点是什么？”）",
			unknownModel: "未知",
		},
		chatUi: {
			activityLog: "活动日志",
			addAttachments: "添加{items}",
			addAttachmentsOr: "添加{items}或{last}",
			audioAttachment: "音频附件",
			auto: "自动",
			cachedInputTokens: "缓存输入 token",
			clickPlusToCreateSkill: "暂无技能。点击 + 创建一个。",
			copiedToClipboard: "已复制到剪贴板",
			copy: "复制",
			copyToClipboard: "复制到剪贴板",
			create: "创建",
			createSkill: "创建技能",
			discount: "折扣",
			document: "文档",
			documents: "文档",
			editAndRetry: "编辑并重试",
			editAndRetryFromHere: "从此处编辑并重试",
			failedToCopyToClipboard: "复制到剪贴板失败",
			forkChat: "Fork 聊天",
			generationStopped: "生成已停止：{reason}",
			generatedImage: "生成的图片",
			howCanIHelp: "我可以帮你什么？",
			inputTokens: "输入 token",
			messagePlaceholder: "输入消息",
			minimal: "最小",
			noSkillsYetCreate: "暂无技能。点击 + 创建一个。",
			outputTokens: "输出 token",
			photos: "图片",
			reasoning: "推理",
			reasoningHigh: "高",
			reasoningLow: "低",
			reasoningMedium: "中",
			regenerateResponse: "重新生成回答",
			removeSkill: "移除技能",
			responseContentFiltered: "回答已被内容政策过滤",
			responseReachedMaxTokens: "回答达到最大 token 限制",
			responseMetadata: "响应元数据",
			retry: "重试",
			selectSkill: "选择技能",
			send: "发送",
			suggestionCode: "代码",
			suggestionCreate: "创建",
			suggestionExplore: "探索",
			suggestionImageGen: "图片生成",
			temporaryChatSubtitle: "临时聊天不会出现在你的聊天历史中。",
			totalCost: "总费用",
			uploadedImage: "上传的图片",
			usedModel: "使用的模型",
			viewAllSkills: "查看全部技能",
		},
		chatPage: {
			addModelForComparison: "添加模型用于对比",
			cannotCreateComparisonChat: "主聊天存在前不能创建对比聊天",
			chatDeleted: "聊天已被删除。请开始新的对话。",
			chatForked: "聊天已 fork",
			chatContextInsufficientCredits:
				"当前聊天仍计费到内置 Chat 组织，该组织已无可用额度。请为 Chat 组织充值，或切换到有余额的组织后重新新建对话。",
			chatContextInsufficientCreditsWithOrganization:
				"当前聊天仍计费到内置 Chat 组织，该组织已无可用额度。你当前有余额的组织是“{organization}”，但这个聊天不会自动使用其额度。请切换到“{organization}”后重新新建对话，或给 Chat 组织充值。",
			chatWindows: "聊天窗口",
			failedCleanupChat: "清理聊天失败：{error}",
			failedCreateChat: "创建新聊天失败。请重试。",
			failedParseAudios: "解析音频失败：{error}",
			failedParseDocuments: "解析文档失败：{error}",
			failedParseImages: "解析图片失败：{error}",
			failedParseSources: "解析来源失败：{error}",
			failedParseTools: "解析工具失败：{error}",
			failedSaveAiResponse: "保存 AI 响应失败：{error}",
			failedSaveAiResponseNoChat:
				"保存 AI 响应失败：未找到聊天 ID（聊天可能在流结束前尚未完成保存）。",
			forkChat: "Fork 聊天",
			forking: "Fork 中...",
			modelLabel: "模型 {index}",
			noChatSelected: "未选择聊天。",
			organizationInsufficientCredits:
				"当前组织“{organization}”已无可用额度。请为该组织充值，或切换到其他有余额的组织。",
			providerLabelAlibaba: "阿里云百炼",
			providerAuthorizationFailed:
				"平台配置的 {provider} 凭证无效、缺失或已失效。请检查供应商密钥和权限配置后重试。",
			providerAuthorizationFailedGeneric:
				"平台配置的供应商凭证无效、缺失或已失效。请检查供应商密钥和权限配置后重试。",
			openAudioStudio: "打开音频工作台",
			openVideoStudio: "打开视频工作台",
			removeWindow: "移除窗口",
			selectModel: "选择模型...",
			switchToAudioDescription:
				"{model} 是语音生成模型。是否在音频工作台中打开？",
			switchToAudioTitle: "切换到音频工作台？",
			switchToVideoDescription:
				"{model} 是视频生成模型。是否在视频工作台中打开？",
			switchToVideoTitle: "切换到视频工作台？",
			syncPromptInput: "同步提示词输入",
			toggleOff: "关",
			toggleOn: "开",
		},
		apiKey: {
			apiKey: "API 密钥",
			apiKeyCopied: "API 密钥已复制",
			apiKeyCopiedDescription: "API 密钥已复制到剪贴板。",
			apiKeyCreated: "API 密钥已创建",
			apiKeyCreatedDescription:
				"你的 API 密钥已创建并应用。请立即复制保存，之后无法再次查看。",
			apiKeyCleared: "API 密钥已清除",
			apiKeyClearedDescription: "你的 API 密钥已清除。",
			apiKeyName: "API 密钥名称",
			apiKeyNamePlaceholder: "例如 Playground Test Key",
			apiKeyRequired: "请输入 API 密钥",
			apiKeySaved: "API 密钥已保存",
			apiKeySavedDescription: "你的 API 密钥已保存并成功启用。",
			apiKeySavedNewDescription: "你的新 API 密钥已保存并启用。",
			clearKey: "清除密钥",
			copyApiKey: "复制 API 密钥",
			createApiKey: "创建 API 密钥",
			editKey: "编辑密钥",
			enterApiKey: "输入你的 API 密钥",
			enterApiKeyDescription:
				"要使用 Playground，你需要提供自己的 doteb API 密钥。",
			enterApiKeyPlaceholder: "输入你的 doteb API 密钥...",
			invalidApiKey: "API 密钥无效",
			invalidApiKeyDescription: "请检查你的 API 密钥后重试。",
			invalidApiKeyShort: "API 密钥无效。请检查密钥后重试。",
			keyRequiredCheck: "需要 API 密钥。请检查密钥后重试。",
			localStorageDescription:
				"你的 API 密钥只会保存在浏览器本地，并仅用于代表你向 doteb API 发起请求。",
			manageApiKey: "管理你的 API 密钥",
			manageApiKeyDescription: "查看、编辑或清除已保存的 API 密钥。",
			pasteExistingKey: "粘贴已有密钥",
			createNewKey: "创建新密钥",
			saveApiKey: "保存 API 密钥",
			secureStorageReminder: "请务必安全保存此 API 密钥。",
			useCreditsDescription:
				"输入你的 doteb API 密钥，使用额度开始与 AI 模型聊天。",
			validApiKeyRequired: "请输入有效的 API 密钥",
			validationFailed: "API 密钥验证失败：{status} {statusText}",
			yourApiKey: "你的 API 密钥",
		},
		mcp: {
			activeCount: "MCP 服务器（{count} 个已启用）",
			addServer: "添加 MCP 服务器",
			apiKeyOptional: "API 密钥（可选）",
			deleteServer: "删除服务器",
			description:
				"连接到 Model Context Protocol 服务器，用外部工具扩展 AI 能力。",
			editServer: "编辑服务器",
			emptyDescription: "添加服务器以扩展 AI 能力",
			emptyTitle: "尚未配置 MCP 服务器",
			hideApiKey: "隐藏 API 密钥",
			name: "名称",
			save: "保存",
			showApiKey: "显示 API 密钥",
			title: "MCP 服务器",
			url: "URL",
		},
		skills: {
			deleteConfirm: "确定要删除“{name}”吗？此操作无法撤销。",
			deleteSkill: "删除技能",
			deleting: "删除中...",
			description: "描述",
			descriptionPlaceholder: "描述何时使用这个技能...",
			createDescription: "创建可复用的指令集，在特定上下文中指导 AI。",
			createSkill: "创建",
			descriptionExample: "将品牌规范应用到演示文稿和文档中...",
			editSkill: "编辑技能",
			fileRequirements: "文件要求",
			fileSizeLimit: "文件大小不得超过 50 MB",
			generateAndSave: "生成并保存",
			generateCreated: "技能“{name}”已创建",
			generateDescription:
				"描述你希望技能完成的任务，doteb 会为你编写并保存。之后你仍可编辑。",
			generatePromptLabel: "它应该做什么？",
			generatePromptPlaceholder:
				"一个用友好语气审查我的拉取请求安全问题并建议修复的技能...",
			generateTitle: "用 AI 生成技能",
			generateWithAi: "用 AI 生成",
			instructions: "说明",
			instructionsPlaceholder: "输入技能说明，支持标题、列表等格式...",
			loadingSkills: "正在加载技能...",
			mySkills: "我的技能",
			name: "名称",
			namePlaceholder: "品牌规范",
			rawView: "源码视图",
			renderedView: "渲染视图",
			saving: "保存中...",
			selectSkill: "选择一个技能查看详情",
			title: "技能",
			uploadDropzone: "拖放或点击上传",
			uploadFile: "上传技能文件",
			uploadMarkdownRequirement:
				".md 文件必须在 YAML frontmatter 中包含 `name` 字段",
			uploadParseError:
				"无法解析技能。.md 文件必须包含带 `name` 字段的 YAML frontmatter。",
			uploadSkill: "上传技能",
			uploadTitle: "上传技能",
			uploadUnsupportedFile:
				"当前仅支持上传 .md 文件，.zip/.skill 支持即将推出。",
			uploadZipRequirement: ".zip 或 .skill 文件必须包含 SKILL.md 文件",
			viewAllSkills: "查看全部技能",
			writeSkill: "编写技能",
			writeSkillInstructions: "编写技能说明",
		},
		authDialog: {
			canvasDescription: "通过实时输出构建和预览 UI",
			description:
				"跨 210+ 模型进行聊天、图片、视频、画布和群聊对比。免费开始，无需信用卡。",
			groupChatDescription: "并排运行多个模型并对比结果",
			highlightHistory: "保存并搜索完整聊天历史",
			highlightPlans: "可按量使用额度，也可选择固定月付套餐",
			imageDescription: "用提示词生成和编辑图片",
			title: "所有模型与工作台，集中在一个入口",
			videoDescription: "把提示词转成短视频",
			audioDescription: "把文本转成自然语音",
			chatDescription: "GPT、Claude、Gemini 和 210+ 模型，可在对话中切换",
		},
		entities: {
			createOrganization: "创建新组织",
			createOrganizationDescription: "创建新组织来归类你的项目。",
			createOrganizationSubmit: "创建组织",
			createProject: "创建新项目",
			createProjectDescription: "在 {organization} 中创建新项目。",
			createProjectSubmit: "创建项目",
			failedCreateOrganization: "创建组织失败",
			failedCreateProject: "创建项目失败",
			name: "名称",
			newProject: "新项目",
			noProjectsYet: "暂无项目",
			organizationCreated: "组织创建成功",
			organizationCreatedDescription: "{name} 已创建。",
			organizationLimitReached: "已达到组织数量上限",
			organizationLimitReachedDescription:
				"你已达到当前套餐可创建的组织数量上限。请联系我们升级到 Enterprise。",
			projectCreated: "项目创建成功",
			projectCreatedDescription: "{name} 已创建。",
			projectLimitReached: "已达到项目数量上限",
			projectLimitReachedDescription:
				"你已达到当前套餐可创建的项目数量上限。请联系我们升级到 Enterprise。",
			selectedOrganizationFallback: "所选组织",
			selectProject: "选择项目",
			projectsIn: "{organization} 中的项目",
			organizationFallback: "组织",
			organizations: "组织",
			chatPlan: "聊天套餐",
		},
	},
} satisfies Partial<Record<Locale, TranslationTree>>;
